/* ============================================
   THESIS BOOK READER

   Presents the doctoral thesis as a readable book without
   ever serving the source PDF. Pages are pre-rendered to
   WebP images at build time (see scripts/build-thesis-pages.mjs)
   and described by a manifest, so the browser only ever
   receives the individual page images it is displaying.

   - "Book" mode: two-page spread with a page-curl animation.
   - "Pages" mode: one page at a time, larger and scrollable.

   Both modes are view-only: there is no PDF to download, no
   text layer to copy, and context menu, drag, selection,
   save, copy and print are all suppressed.
   ============================================ */

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import HTMLFlipBook from "react-pageflip";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Lock,
  Maximize2,
  Minimize2,
  RotateCcw,
  Rows3,
  Scan,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "@styles/book-reader.css";

const DEFAULT_MANIFEST = "/assets/thesis_pages/manifest.json";
const FALLBACK_RATIO = 842 / 595; // A4 portrait
const PRELOAD_AHEAD = 6; // pages warmed ahead of the current spread
const PRELOAD_BEHIND = 2;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;
const STORAGE_KEY = "gemini-thesis-book-state";

const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

const blockEvent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

/* ------------------------------------------------
   Manifest helpers
   ------------------------------------------------ */

const padPage = (n) => String(n).padStart(4, "0");

const buildSrc = (manifest, tier, pageNumber) =>
  manifest.pathTemplate
    .replace("{tier}", tier)
    .replace("{page}", padPage(pageNumber));

/**
 * Smallest tier that still covers the pixels we are about to paint,
 * so page turns stay light until the reader zooms in.
 */
const pickTier = (manifest, cssWidth) => {
  const needed = cssWidth * Math.min(window.devicePixelRatio || 1, 2);
  const tiers = [...manifest.tiers].sort((a, b) => a.width - b.width);
  return (tiers.find((t) => t.width >= needed) ?? tiers[tiers.length - 1]).name;
};

/**
 * Swaps to a new image only once it has decoded, so changing page or
 * resolution never leaves a blank white frame on screen.
 */
const usePreloadedSrc = (src) => {
  const [shown, setShown] = useState(null);

  useEffect(() => {
    if (!src || src === shown) return undefined;
    let cancelled = false;
    let img;

    // A page that fails to decode is retried once past the HTTP cache,
    // which clears out any stale response left by an earlier build.
    const attempt = (url, retryLeft) => {
      img = new Image();
      img.onload = () => {
        if (!cancelled) setShown(src);
      };
      img.onerror = () => {
        if (cancelled) return;
        if (retryLeft > 0) attempt(`${src}?reload=${Date.now()}`, 0);
      };
      img.src = url;
    };

    attempt(src, 1);

    return () => {
      cancelled = true;
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };
  }, [src, shown]);

  return [shown, Boolean(src) && shown !== src];
};

/* ------------------------------------------------
   Flip view page
   ------------------------------------------------ */

const BookNavContext = createContext({
  manifest: null,
  currentIndex: 0,
  tier: "md",
});

const BookPage = forwardRef(function BookPage({ pageNumber }, ref) {
  const { manifest, currentIndex, tier } = useContext(BookNavContext);
  const offset = pageNumber - 1 - currentIndex;
  const inWindow = offset <= PRELOAD_AHEAD && offset >= -PRELOAD_BEHIND;

  // Outside the window nothing new is requested, but `usePreloadedSrc`
  // keeps the last decoded image, so flipping back is instant.
  const wanted =
    manifest && inWindow ? buildSrc(manifest, tier, pageNumber) : null;
  const [src] = usePreloadedSrc(wanted);

  return (
    <div ref={ref} className="thesis-book-page" data-density="soft">
      {src ? (
        <img src={src} alt={`Thesis page ${pageNumber}`} draggable={false} />
      ) : (
        <div className="thesis-book-page-blank">
          <Loader2 className="w-4 h-4 animate-spin mb-2" />
          {pageNumber}
        </div>
      )}
    </div>
  );
});

/* ------------------------------------------------
   Single page view
   ------------------------------------------------ */

const SinglePageView = ({ manifest, currentIndex, displayWidth, tier }) => {
  const scrollerRef = useRef(null);
  const wanted = buildSrc(manifest, tier, currentIndex + 1);
  const [src, isPending] = usePreloadedSrc(wanted);

  // Return to the top of the page when the page itself changes,
  // but stay put while only the zoom level changes.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [currentIndex]);

  const height = Math.round(displayWidth * manifest.aspectRatio);

  return (
    <div ref={scrollerRef} className="thesis-scroll-view">
      <div className="thesis-scroll-page-slot">
        <div
          className="thesis-scroll-page-frame"
          style={{ width: displayWidth, height }}
        >
          {src ? (
            <img
              src={src}
              alt={`Thesis page ${currentIndex + 1}`}
              draggable={false}
            />
          ) : (
            <div className="thesis-page-placeholder">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {isPending && src && (
            <div className="thesis-pending-badge">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Page {currentIndex + 1}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3 mb-1 text-center">
          Page {currentIndex + 1} of {manifest.pages}
        </p>
      </div>
    </div>
  );
};

/* ------------------------------------------------
   Persisted reader state
   ------------------------------------------------ */

const loadSavedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/* ------------------------------------------------
   Main reader
   ------------------------------------------------ */

const ThesisBookReader = ({
  manifestUrl = DEFAULT_MANIFEST,
  title = "PhD Thesis Book",
}) => {
  const bookRef = useRef(null);
  const stageRef = useRef(null);
  const rootRef = useRef(null);
  const restoredRef = useRef(false);

  // Read once, so a later write does not change this session's starting point
  const [savedIndex] = useState(() => loadSavedState().index);
  const [manifest, setManifest] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageWidth, setPageWidth] = useState(420);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHot, setIsHot] = useState(false);
  const [bookReady, setBookReady] = useState(false);
  // Only set while the reader is typing in the page box; otherwise the
  // field simply follows the current page.
  const [jumpDraft, setJumpDraft] = useState(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [viewMode, setViewMode] = useState(() =>
    loadSavedState().viewMode === "scroll" ? "scroll" : "flip",
  );
  const [zoom, setZoom] = useState(() => {
    const saved = loadSavedState().zoom;
    return typeof saved === "number" ? clampZoom(saved) : 1;
  });

  const numPages = manifest?.pages ?? 0;
  const ratio = manifest?.aspectRatio ?? FALLBACK_RATIO;
  const pageHeight = Math.round(pageWidth * ratio);

  /* ---- start loading only when the reader nears the viewport ---- */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "500px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ---- manifest ---- */
  useEffect(() => {
    if (!shouldLoad) return undefined;
    let cancelled = false;

    // A dev-server restart or a stale cached HTML fallback can make the
    // first request fail, so one silent retry precedes the error state.
    const fetchManifest = async () => {
      const res = await fetch(manifestUrl, { cache: "no-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${manifestUrl}`);

      const type = res.headers.get("content-type") ?? "";
      if (!type.includes("json")) {
        throw new Error(
          `Expected JSON at ${manifestUrl} but received "${type}". ` +
            "Run `npm run thesis:pages` to generate the thesis pages.",
        );
      }

      const data = await res.json();
      if (!data?.pages || !data?.tiers?.length || !data?.pathTemplate) {
        throw new Error("Thesis manifest is missing required fields");
      }
      return data;
    };

    const load = async () => {
      for (let tryIndex = 0; tryIndex < 2; tryIndex += 1) {
        try {
          const data = await fetchManifest();
          if (cancelled) return;
          setManifest(data);
          if (
            typeof savedIndex === "number" &&
            savedIndex > 0 &&
            savedIndex < data.pages
          ) {
            setCurrentIndex(savedIndex);
          }
          return;
        } catch (err) {
          if (cancelled) return;
          if (tryIndex === 1) {
            console.error("Thesis book could not be opened:", err);
            setError("The thesis book could not be opened. Please try again.");
            return;
          }
          await new Promise((r) => window.setTimeout(r, 600));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, manifestUrl, attempt, savedIndex]);

  const retry = useCallback(() => {
    setError(null);
    setManifest(null);
    setBookReady(false);
    setAttempt((a) => a + 1);
  }, []);

  /* ---- responsive page size ---- */
  const measure = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const available = el.clientWidth;
    const width =
      viewMode === "scroll"
        ? Math.max(280, Math.min(available - 24, isFullscreen ? 980 : 820))
        : Math.max(
            160,
            Math.min(
              Math.floor((available - 32) / 2),
              isFullscreen ? 620 : 500,
            ),
          );
    setPageWidth((prev) => (prev === width ? prev : width));
  }, [isFullscreen, viewMode]);

  useEffect(() => {
    measure();
    const el = stageRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, manifest, shouldLoad]);

  /* ---- persist reading position ---- */
  useEffect(() => {
    if (!numPages) return undefined;
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ index: currentIndex, zoom, viewMode }),
        );
      } catch {
        /* storage unavailable */
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [currentIndex, zoom, viewMode, numPages]);

  /* ---- flip book ---- */
  const getFlip = useCallback(() => bookRef.current?.pageFlip?.() ?? null, []);

  const onFlip = useCallback((e) => setCurrentIndex(e.data), []);

  const onInit = useCallback(() => setBookReady(true), []);

  // `startPage` is not always honoured once the book has been built, so
  // nudge it to the remembered page the first time it becomes ready.
  useEffect(() => {
    if (viewMode !== "flip") {
      // Book is unmounted in Pages mode; restore again when we return.
      restoredRef.current = false;
      return;
    }
    if (!bookReady || restoredRef.current) return;
    restoredRef.current = true;
    const flip = getFlip();
    if (
      flip &&
      currentIndex > 0 &&
      flip.getCurrentPageIndex() !== currentIndex
    ) {
      flip.turnToPage(currentIndex);
    }
  }, [viewMode, bookReady, currentIndex, getFlip]);

  // Falls back to enabling the controls if the book never reports init.
  useEffect(() => {
    if (!numPages || viewMode !== "flip") return undefined;
    const timer = window.setTimeout(() => setBookReady(true), 1200);
    return () => window.clearTimeout(timer);
  }, [numPages, viewMode]);

  /* ---- navigation ---- */
  const goPrev = useCallback(() => {
    if (viewMode === "flip") getFlip()?.flipPrev("top");
    else setCurrentIndex((i) => Math.max(i - 1, 0));
  }, [viewMode, getFlip]);

  const goNext = useCallback(() => {
    if (viewMode === "flip") getFlip()?.flipNext("top");
    else setCurrentIndex((i) => Math.min(i + 1, numPages - 1));
  }, [viewMode, getFlip, numPages]);

  const goToPage = useCallback(
    (pageOneBased) => {
      if (!numPages) return;
      const idx = Math.max(0, Math.min(pageOneBased - 1, numPages - 1));
      setCurrentIndex(idx);
      if (viewMode === "flip") getFlip()?.turnToPage(idx);
    },
    [numPages, viewMode, getFlip],
  );

  /* ---- zoom ---- */
  const zoomIn = useCallback(
    () =>
      setZoom((z) => clampZoom(Math.ceil((z + 0.001) / ZOOM_STEP) * ZOOM_STEP)),
    [],
  );
  const zoomOut = useCallback(
    () =>
      setZoom((z) =>
        clampZoom(Math.floor((z - 0.001) / ZOOM_STEP) * ZOOM_STEP),
      ),
    [],
  );
  const resetZoom = useCallback(() => setZoom(1), []);

  const fitToPage = useCallback(() => {
    const el = stageRef.current;
    if (!el || pageWidth < 80) return;
    const availW = Math.max(120, el.clientWidth - 32);
    const availH = Math.max(
      120,
      el.clientHeight - (viewMode === "scroll" ? 56 : 16),
    );
    const contentW = viewMode === "flip" ? pageWidth * 2 : pageWidth;
    setZoom(
      clampZoom(Math.min(availW / contentW, availH / (pageWidth * ratio))),
    );
    window.requestAnimationFrame(() => {
      el.scrollTo({ top: 0, left: 0, behavior: "auto" });
      el.querySelector(".thesis-scroll-view")?.scrollTo({ top: 0, left: 0 });
    });
  }, [pageWidth, viewMode, ratio]);

  /* ---- keyboard (active while hovering or in fullscreen) ---- */
  useEffect(() => {
    if (!isHot && !isFullscreen) return undefined;

    const onKey = (e) => {
      if (e.target?.tagName === "INPUT") return;

      // View-only: swallow save / print / copy shortcuts over the reader
      if (
        (e.ctrlKey || e.metaKey) &&
        ["s", "p", "c", "x", "a"].includes(e.key?.toLowerCase())
      ) {
        e.preventDefault();
        return;
      }

      switch (e.key) {
        case "=":
        case "+":
          e.preventDefault();
          zoomIn();
          break;
        case "-":
        case "_":
          e.preventDefault();
          zoomOut();
          break;
        case "0":
          e.preventDefault();
          resetZoom();
          break;
        case "Escape":
          if (isFullscreen) setIsFullscreen(false);
          break;
        case "Home":
          e.preventDefault();
          goToPage(1);
          break;
        case "End":
          e.preventDefault();
          goToPage(numPages);
          break;
        case "ArrowRight":
        case "PageDown":
          e.preventDefault();
          goNext();
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          goPrev();
          break;
        default:
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    isHot,
    isFullscreen,
    goNext,
    goPrev,
    goToPage,
    numPages,
    zoomIn,
    zoomOut,
    resetZoom,
  ]);

  /* ---- fullscreen body lock ---- */
  useEffect(() => {
    document.body.style.overflow = isFullscreen ? "hidden" : "";
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
      measure();
    }, 40);
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = "";
    };
  }, [isFullscreen, measure]);

  /* ---- ctrl/cmd + wheel zoom ---- */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (e.deltaY < 0) zoomIn();
      else zoomOut();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomIn, zoomOut, shouldLoad, viewMode, isFullscreen]);

  /* ---- derived values ---- */
  const scrollDisplayWidth = Math.round(pageWidth * zoom);
  const tier = useMemo(() => {
    if (!manifest) return "md";
    return pickTier(
      manifest,
      viewMode === "flip" ? pageWidth * zoom : scrollDisplayWidth,
    );
  }, [manifest, viewMode, pageWidth, zoom, scrollDisplayWidth]);

  const navValue = useMemo(
    () => ({ manifest, currentIndex, tier }),
    [manifest, currentIndex, tier],
  );

  const pages = useMemo(() => {
    if (!numPages) return null;
    return Array.from({ length: numPages }, (_, i) => (
      <BookPage key={i} pageNumber={i + 1} />
    ));
  }, [numPages]);

  const isLoading = !manifest || (viewMode === "flip" && !bookReady);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < numPages - 1;

  const navBtn =
    "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium " +
    "bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white " +
    "disabled:opacity-30 disabled:cursor-not-allowed transition-colors";
  const iconBtn =
    "p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 " +
    "disabled:opacity-30 disabled:cursor-not-allowed transition-colors";

  if (!shouldLoad) {
    return (
      <div ref={rootRef}>
        <div className="thesis-book-desk rounded-2xl p-8 min-h-[420px] flex flex-col items-center justify-center text-gray-400">
          <BookOpen className="w-8 h-8 text-indigo-400 mb-3" />
          <p className="text-sm">
            The thesis book will open as you scroll here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      <div
        className={`thesis-book-desk rounded-2xl ${
          isFullscreen
            ? "is-fullscreen fixed inset-0 z-[110] flex flex-col p-4 md:p-6"
            : "p-4 md:p-8"
        }`}
        onMouseEnter={() => setIsHot(true)}
        onMouseLeave={() => {
          if (!isFullscreen) setIsHot(false);
        }}
        onContextMenu={blockEvent}
        onDragStart={blockEvent}
        onCopy={blockEvent}
        onCut={blockEvent}
      >
        {isFullscreen && (
          <div className="flex items-center justify-between mb-4 px-1">
            <div className="flex items-center gap-2 text-white">
              <BookOpen className="w-5 h-5 text-indigo-300" />
              <span className="font-medium text-sm md:text-base">{title}</span>
            </div>
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-red-500/20 transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Stage. The scrolling area is wrapped so the edge arrows and
            the loading overlay stay put while a zoomed page pans. */}
        <div
          className={`relative ${isFullscreen ? "flex-1 min-h-0" : ""}`}
          style={
            isFullscreen
              ? undefined
              : viewMode === "scroll"
                ? { height: "min(80vh, 920px)" }
                : { height: pageHeight + 16 }
          }
        >
          <div
            ref={stageRef}
            className={`thesis-book-stage h-full ${
              viewMode === "scroll" ? "is-scroll-mode" : ""
            }`}
          >
            {manifest && viewMode === "scroll" && (
              <SinglePageView
                manifest={manifest}
                currentIndex={currentIndex}
                displayWidth={scrollDisplayWidth}
                tier={tier}
              />
            )}

            {manifest && pages && viewMode === "flip" && (
              <BookNavContext.Provider value={navValue}>
                {/* Outer box reserves the scaled footprint so a zoomed
                  spread can be panned instead of being clipped. */}
                <div
                  className="thesis-flip-outer"
                  style={{
                    width: Math.round(pageWidth * 2 * zoom),
                    height: Math.round(pageHeight * zoom),
                  }}
                >
                  <div
                    style={{
                      width: pageWidth * 2,
                      height: pageHeight,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  >
                    <HTMLFlipBook
                      ref={bookRef}
                      className="thesis-html-flipbook"
                      width={pageWidth}
                      height={pageHeight}
                      size="stretch"
                      minWidth={160}
                      maxWidth={pageWidth}
                      minHeight={Math.round(160 * ratio)}
                      maxHeight={pageHeight}
                      startPage={currentIndex}
                      drawShadow
                      flippingTime={600}
                      usePortrait
                      showCover
                      autoSize
                      maxShadowOpacity={0.5}
                      mobileScrollSupport
                      useMouseEvents
                      showPageCorners
                      clickEventForward={false}
                      disableFlipByClick={false}
                      startZIndex={2}
                      swipeDistance={24}
                      renderOnlyPageLengthChange
                      onFlip={onFlip}
                      onInit={onInit}
                    >
                      {pages}
                    </HTMLFlipBook>
                  </div>
                </div>
              </BookNavContext.Provider>
            )}
          </div>

          {/* Edge arrows — always reachable, in both views */}
          <button
            type="button"
            onClick={goPrev}
            disabled={!canPrev || isLoading}
            className="thesis-edge-arrow left-2 md:left-3"
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canNext || isLoading}
            className="thesis-edge-arrow right-2 md:right-3"
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {(isLoading || error) && (
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-400 rounded-xl ${
                manifest && !error
                  ? "pointer-events-none bg-[#0a0a0f]/40"
                  : "bg-[#0a0a0f]/70"
              }`}
            >
              {error ? (
                <>
                  <p className="text-sm text-center px-6">{error}</p>
                  <button
                    type="button"
                    onClick={retry}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                      bg-indigo-500/20 border border-indigo-500/40 text-indigo-200
                      hover:bg-indigo-500/30 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Try again
                  </button>
                </>
              ) : (
                <>
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                  <span className="text-sm">
                    {manifest ? "Opening the book…" : "Preparing the book…"}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col items-center gap-3 mt-6">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {/* View mode */}
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              <button
                type="button"
                onClick={() => setViewMode("flip")}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                  viewMode === "flip"
                    ? "bg-indigo-500/30 text-white"
                    : "bg-white/5 text-gray-400 hover:text-white"
                }`}
                title="Two-page book with flip animation"
              >
                <BookOpen className="w-4 h-4" />
                Book
              </button>
              <button
                type="button"
                onClick={() => setViewMode("scroll")}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                  viewMode === "scroll"
                    ? "bg-indigo-500/30 text-white"
                    : "bg-white/5 text-gray-400 hover:text-white"
                }`}
                title="One large page at a time"
              >
                <Rows3 className="w-4 h-4" />
                Pages
              </button>
            </div>

            {/* Navigation */}
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={!canPrev}
              className={iconBtn}
              title="First page (Home)"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev || isLoading}
              className={navBtn}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={numPages || 1}
                value={jumpDraft ?? String(currentIndex + 1)}
                onChange={(e) => setJumpDraft(e.target.value)}
                onBlur={() => {
                  if (jumpDraft !== null) {
                    goToPage(parseInt(jumpDraft, 10) || 1);
                    setJumpDraft(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                aria-label="Go to page"
                className="w-16 text-center text-sm font-mono bg-white/5 border border-white/10
                  rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-indigo-500
                  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-sm text-gray-500">
                of {numPages || "—"}
              </span>
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={!canNext || isLoading}
              className={navBtn}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => goToPage(numPages)}
              disabled={!canNext}
              className={iconBtn}
              title="Last page (End)"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>

            {/* Zoom */}
            <div className="flex items-center rounded-lg border border-white/10 overflow-hidden">
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= ZOOM_MIN}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                title="Zoom out (-)"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={resetZoom}
                className="px-2 text-xs font-mono text-gray-300 min-w-[3.25rem] text-center
                  hover:text-white transition-colors"
                title="Reset zoom (0)"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= ZOOM_MAX}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                title="Zoom in (+)"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            <button
              type="button"
              onClick={fitToPage}
              className={navBtn}
              title="Fit the whole page in view"
            >
              <Scan className="w-4 h-4" />
              Fit to page
            </button>

            <button
              type="button"
              onClick={() => setIsFullscreen((v) => !v)}
              className={iconBtn}
              title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5" />
              ) : (
                <Maximize2 className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Page slider */}
          {numPages > 0 && (
            <input
              type="range"
              min={1}
              max={numPages}
              value={currentIndex + 1}
              onChange={(e) => goToPage(Number(e.target.value))}
              className="w-full max-w-md accent-indigo-400"
              aria-label="Jump to page"
            />
          )}

          <p className="text-center text-xs text-gray-600">
            {viewMode === "flip"
              ? "Drag a page corner to turn · Arrow keys navigate · +/− to zoom, 0 to reset"
              : "Scroll inside the page to read · Arrow keys turn pages · Your reading position is remembered"}
          </p>

          <p className="flex items-center justify-center gap-1.5 text-center text-[0.7rem] text-gray-600">
            <Lock className="w-3 h-3" />
            View only — this dissertation is not available for download or
            printing
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThesisBookReader;
